/**
 * Time formatting utilities for displaying durations and relative timestamps.
 */
import { useState, useEffect } from 'react';

/**
 * Formats a duration in seconds into a human-readable string (e.g., "2d 5h 30m 10s").
 */
export const formatDuration = (seconds: number): string => {
    if (seconds < 0) seconds = 0;

    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`);

    return parts.join(' ');
};

/**
 * Hook that returns a live-updating elapsed time string since the given start time.
 * Updates every second.
 */
export const useElapsedTime = (startTime: string): string => {
    const [elapsed, setElapsed] = useState<string>('');

    useEffect(() => {
        const update = () => {
            const start = new Date(startTime).getTime();
            const now = Date.now();
            const diffSeconds = Math.floor((now - start) / 1000);
            setElapsed(formatDuration(diffSeconds));
        };

        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [startTime]);

    return elapsed;
};

/**
 * Hook that returns a live-updating "time ago" string (e.g., "5m 30s ago").
 * Updates every second.
 */
export const useTimeAgo = (timestamp: string): string => {
    const [ago, setAgo] = useState<string>('');

    useEffect(() => {
        const update = () => {
            const then = new Date(timestamp).getTime();
            const now = Date.now();
            const diffSeconds = Math.floor((now - then) / 1000);
            setAgo(formatDuration(diffSeconds) + ' ago');
        };

        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [timestamp]);

    return ago;
};
